import * as React from 'react';
import { useSelector } from 'react-redux';
import { CircleArrowUp, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

import { RootState } from '../../../reducers';
import usePlan from '../../plan/hooks/plan';
import useLimits from '../../plan/hooks/limits';
import { requestStatusType } from '../../../types';
import { useMediaQuery } from '../../../hooks/use-media-query';

const UpgradeButton = () => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 640px)');
  const guardLimits = useLimits();
  const { plan } = useSelector((state: RootState) => state);
  const { upgradeStatus } = plan.subscription;
  const { planChangeDirection } = plan;
  const prevStatusRef = React.useRef<requestStatusType>('idle');

  const handleClick = () => {
    guardLimits('generalUpgrade', () => {});
  };

  const { isPlan } = usePlan();
  const upgraded = isPlan(['GROWTH', 'BUSINESS', 'ENTERPRISE']);
  let status = upgradeStatus || 'idle';
  if (
    !upgraded &&
    (['failed', 'succeeded'] as requestStatusType[]).includes(upgradeStatus)
  ) {
    status = 'idle';
  }

  // Show toast when status changes
  React.useEffect(() => {
    if (
      status !== 'idle' &&
      status !== 'succeeded' &&
      status !== prevStatusRef.current
    ) {
      const tooltipKey =
        planChangeDirection === 'downgrade'
          ? `upgrade.tooltips.downgrade_${status}`
          : `upgrade.tooltips.${status}`;
      const message = t(tooltipKey);
      if (status === 'failed') {
        toast.error(message);
      } else {
        toast(message);
      }
    }
    prevStatusRef.current = status;
  }, [status, t, planChangeDirection]);

  if (upgraded) {
    return null;
  }

  // Determine the icon to display based on the status
  const Icon = status === 'succeeded' ? CheckCircle : CircleArrowUp;

  return (
    <Button
      variant="default"
      size={isMobile ? 'icon' : 'default'}
      onClick={handleClick}
      disabled={status === 'pending'}
      className={
        isMobile
          ? 'h-9 w-9 p-0 shadow-sm transition-all duration-300 ease-in-out'
          : 'pl-3 pr-4 shadow-sm transition-all duration-300 ease-in-out'
      }
    >
      <Icon className="h-4 w-4" />
      {!isMobile && t(`upgrade.buttons.${status}`).replace(/ /g, '\u00A0')}
    </Button>
  );
};

export default UpgradeButton;
